/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터 테이블 블록을 정의한다.
 *
 * **`elements/grid/Grid.ts`(Element)는 제거됐다.** 예전엔 진짜 `<table>` DOM을 Grid 라는 별도
 * Element가 통째로 떠안아, 드래그 추적·구조변경·직렬화·히스토리 기록을 전부 자체 구현해야 했다
 * (`iModules` 쪽 v1 `Table.mjs`가 그 실제 분량을 보여준다). 대신 이 블록은 Cell(atomic 블록,
 * `blocks/table/blocks/Cell.ts`) 여러 개를 담는 `Container`(`display:grid`) 하나만 소유한다 —
 * 다중 셀 드래그선택·구조변경 후 히스토리 기록·셀 값 직렬화가 Container/Selection/History의
 * 범용 구현으로 대부분 흡수된다는 게 이 재설계의 핵심이다. 전체 설계 논의와 근거는
 * `Editor.v3.Table.md` 참고.
 *
 * **이 커밋은 1차 구현(시각적 골격)까지만 다룬다** — 테이블 블록 버튼을 누르면 기존
 * `wysiwyg_bk`와 동일하게 보이는 3×3 그리드 + 컨트롤 거터(코너/열헤더/행헤더)가 뜨는 것까지가
 * 목표다. 드래그선택 배선, 행/열 추가·삭제, 병합, 캐럿 이동, 직렬화는 각 메서드에 "구현 예정"
 * 스텁으로 남겨뒀다.
 *
 * @file /src/rootin/blocks/table/Table.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 20.
 */
import { Block } from '@rootin/base/Block';
import { BlockClass } from '@rootin/base/Blocks';
import { Container } from '@rootin/elements/container/Container';
import { Cell } from '@rootin/blocks/table/blocks/Cell';
import { Merge } from './tools/Merge';
import { Split } from './tools/Split';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Menu } from '@rootin/core/Menu';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import './Table.css';

export class Table extends Block {
    /**
     * 블록타입
     */
    static type = 'table';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sheet-icon lucide-sheet"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><line x1="3" x2="21" y1="9" y2="9"/><line x1="3" x2="21" y1="15" y2="15"/><line x1="9" x2="9" y1="9" y2="21"/><line x1="15" x2="15" y1="9" y2="21"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.table.label');

    /**
     * 기본으로 생성되는 테이블 크기 (1차 구현은 고정 3×3 — 행/열 추가 구현 시 가변으로 바뀐다)
     */
    static defaultRows = 3;
    static defaultCols = 3;

    /**
     * Cell 블록만 담는 컨테이너. 유저가 임의로 블록을 넣고 빼는 영역이 아니라 행/열 추가 버튼을
     * 통해서만 채워지므로(§A.2), `insertTarget`/`autoSeed`/`createMarker`/`addHandler`를 전부
     * 기본값(끔)으로 둔다 — Container 자체엔 "블록 추가 UI" 표면이 아예 없다.
     */
    body: Container;

    /**
     * 지금 그려진 행/열 갯수. Cell의 `grid-row`/`grid-column`뿐 아니라 컨트롤 거터 갯수·
     * `grid-template-*` 계산에도 쓰인다.
     */
    numRows: number;
    numCols: number;

    /**
     * 에디터 테이블 블록을 정의한다.
     * 생성이 비동기 `render()`를 필요로 하므로 생성자에서 끝낼 수 없음 — Images/Item과 동일한
     * 이유).
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);

        this.numRows = Table.defaultRows;
        this.numCols = Table.defaultCols;

        this.body = new Container(this, {
            name: 'body',
            blocks: ['table-cell'],
            insertTarget: false,
            autoSeed: false,
        });
        this.$getBlock().append(this.body.$getElement());
    }

    /**
     * 블록을 초기화한다. 1차 구현 범위(시각 골격)엔 컨테이너 레벨 mousedown/click 위임이 필요
     * 없으므로 기본 구현(블록추가 버튼 배선)만 그대로 쓴다 — 드래그선택 배선이 붙는 시점에
     * override해서 `onCellMousedown`/`onControlClick` 위임을 추가한다.
     *
     * @param editor - 블록이 추가되는 에디터
     * @returns 블록추가 버튼 DOM
     */
    static init(editor: Editor): Dom {
        editor.addTool(new Merge());
        editor.addTool(new Split());

        return super.init(editor);
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다. 셀 블록타입은 이 블록만 알고 있으므로 직접
     * 처리하고, 그 외에는 상위 구현에 위임한다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return type === Cell.type ? (Cell as BlockClass) : super.getBlockClass(type);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.body.render();

        const $body = this.body.$getElement();
        this.#syncGridTemplate();

        for (let row = 0; row < this.numRows; row++) {
            for (let col = 0; col < this.numCols; col++) {
                const cell = new Cell(this.editor, { row, col });
                $body.append(cell.$getBlock());
                await cell.render();
            }
        }

        this.#renderControls();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.body.isEmpty();
    }

    /**
     * 블록값을 가져온다. `this.body.getValue()`(평탄 목록, 각 항목은 `Cell.getValue()`의
     * `{id, type, row, col, text, rowSpan?, colSpan?}`)를 `row` 기준으로 묶어 `rows[][]`로
     * 재구성한다 — HTML `<table>`로 복원할 때 `rows[r]`를 순서대로 `<td>`로 나열하면 되도록(병합된
     * 칸은 rowspan이 있는 이전 행의 `<td>`가 알아서 덮으므로 별도 처리 없이 그대로 순회 가능),
     * Container/History가 undo·재편집 시 안 바뀐 Cell을 재사용하려면(`id` 매칭) 평탄한 목록이어야
     * 하므로 그 형태는 그대로 두고 Table 경계에서만 모양을 바꾼다(Editor.v3.Table.md 참고).
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        const cells = this.body.getValue() as { row: number; col: number }[];
        const rows: unknown[][] = Array.from({ length: this.numRows }, () => []);
        for (const cell of cells) {
            rows[cell.row].push(cell);
        }
        for (const row of rows) {
            row.sort((a: { col: number }, b: { col: number }) => a.col - b.col);
        }

        return {
            id: this.getId(),
            type: this.getType(),
            numRows: this.numRows,
            numCols: this.numCols,
            rows,
        };
    }

    /**
     * 블록값을 지정한다. `rows[][]`를 평탄화해 `this.body.setValue()`에 넘기면(`Container.
     * setValue()`가 `id` 기준으로 안 바뀐 Cell은 재사용, 새로 생긴 건 만들고, 사라진 건 지운다)
     * Cell 쪽 재구성은 끝난다 — 여기서 남는 일은 `numRows`/`numCols` 갱신과, 그에 따라 달라질 수
     * 있는 grid 트랙·컨트롤 거터 갯수를 다시 맞추는 것뿐이다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        this.numRows = value.numRows;
        this.numCols = value.numCols;

        const cells = (value.rows as unknown[][]).flat();
        await this.body.setValue(cells);

        this.#syncGridTemplate();
        this.#renderControls();
    }

    /**
     * 현재 블록내에 캐럿이 이동될 때 이벤트를 처리한다. 좌우/Tab은 Cell을 담는 Container 안에서
     * 인접 Cell로(문서 순서 = 시각 순서가 아닐 수 있으니 DOM 순서가 아니라 좌표로), 상하는
     * `numCols`칸을 건너뛴 좌표의 Cell로 이동해야 한다.
     *
     * @param direction - 이동 방향
     * @returns 이벤트를 소비했으면 true
     */
    onCaretMove(direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'): boolean {
        throw new Error('구현 예정 — Editor.v3.Table.md §A.3 참고, 좌표 기반 인접 Cell 탐색');
    }

    /**
     * 우클릭 메뉴가 노출될 때 이벤트를 처리한다.
     *
     * @param menu - 메뉴
     * @param e - contextmenu 이벤트
     * @returns 더 이상 메뉴를 변경하지 않도록 차단할지 여부
     */
    onMenu(menu: Menu, e: MouseEvent): boolean {
        throw new Error('구현 예정 — 선택된 Cell 범위 기준 병합/분할/삭제 메뉴 (Editor.v3.Table.md §C 참고)');
    }

    /**
     * `numRows`/`numCols`에 맞춰 grid 트랙을 다시 계산한다. 1열/1행은 컨트롤 거터 전용(`auto`),
     * 나머지가 실제 Cell 트랙이다 — Cell의 `grid-row`/`grid-column`이 전부 `row+2`/`col+2`
     * 기준인 것과 짝을 이룬다(`Cell.syncPosition()` 참고).
     */
    #syncGridTemplate(): void {
        const $body = this.body.$getElement();
        $body.setStyle('grid-template-columns', `auto repeat(${this.numCols}, minmax(0, 1fr))`);
        $body.setStyle('grid-template-rows', `auto repeat(${this.numRows}, auto)`);
    }

    /**
     * 컨트롤 거터(코너 전체선택 버튼 + 열헤더 + 행헤더)를 그린다. 전부 Cell(Block)이 아닌 순수
     * 장식용 DOM이다 — Container의 `blocks:['table-cell']` 제한에 안 걸리고(`Container.#getBlocks()`가
     * `rootin-block`만 골라서 보므로 이 DOM들은 아예 안 보임, Editor.v3.Table.md §A.2 참고),
     * 그러면서도 Cell과 같은 grid 컨테이너의 자식이라 트랙 크기(행 높이/열 너비)가 셀 내용에 맞춰
     * 자동으로 함께 맞춰진다. 버튼들은 아직 클릭 핸들러가 없다 — 행/열 추가·전체/행/열선택은
     * 다음 단계(§B.4) 구현 대상이라 지금은 mousedown의 포커스 이탈 방지만 배선해둔다.
     *
     * `setValue()`가 `numRows`/`numCols`가 달라진 뒤에도 다시 부를 수 있도록, 매번 기존 거터
     * DOM을 먼저 지우고 새로 그린다(멱등) — Cell(Block)과 달리 이 DOM들은 `Container.setValue()`의
     * id 매칭 대상이 아니라서 그쪽이 알아서 정리해주지 않는다.
     */
    #renderControls(): void {
        const $body = this.body.$getElement();
        Html.all('> [data-role^="table-"]', $body).forEach(($el) => $el.remove());

        const $all = Html.create('div', { 'data-role': 'table-ctrl-all' });
        $all.setStyle('grid-row', '1');
        $all.setStyle('grid-column', '1');
        const $allButton = Html.create('button', { type: 'button', 'data-role': 'table-ctrl-all-button' });
        $allButton.on('mousedown', (_, e) => e.preventDefault());
        $all.append($allButton);
        $body.append($all);

        for (let col = 0; col < this.numCols; col++) {
            const $item = Html.create('div', { 'data-role': 'table-col-item', 'data-col': String(col) });
            $item.setStyle('grid-row', '1');
            $item.setStyle('grid-column', String(col + 2));

            const $select = Html.create('button', { type: 'button', 'data-role': 'table-col-select' });
            $select.on('mousedown', (_, e) => e.preventDefault());
            $item.append($select);

            const $add = Html.create('button', { type: 'button', 'data-role': 'table-col-add' }, '<i></i>');
            $add.on('mousedown', (_, e) => e.preventDefault());
            $item.append($add);

            $body.append($item);
        }

        for (let row = 0; row < this.numRows; row++) {
            const $item = Html.create('div', { 'data-role': 'table-row-item', 'data-row': String(row) });
            $item.setStyle('grid-row', String(row + 2));
            $item.setStyle('grid-column', '1');

            const $select = Html.create('button', { type: 'button', 'data-role': 'table-row-select' });
            $select.on('mousedown', (_, e) => e.preventDefault());
            $item.append($select);

            const $add = Html.create('button', { type: 'button', 'data-role': 'table-row-add' }, '<i></i>');
            $add.on('mousedown', (_, e) => e.preventDefault());
            $item.append($add);

            $body.append($item);
        }
    }
}
